import {
  type FieldValues,
  type FormItemWrapperProps,
  type Path,
  type PathValue,
  type UseControllerProps,
  useCustomController,
} from '@faceup/form'
import { UntitledIcon } from '@faceup/icons'
import { ulCheckCircle } from '@faceup/icons/ulCheckCircle'
import { defineMessages, useIntl } from '@faceup/localization'
import { FormItem } from '@faceup/ui'
import { Flex, Input, Typography, useUiBaseConfig } from '@faceup/ui-base'
import { useState } from 'react'

const messages = defineMessages('followUp.components.survey.surveyMultipleChoice', [
  { id: 'otherOption', defaultMessage: 'Other (please specify)' },
] as const)

type Option = {
  label: string
  value: string
}

type UiMultiSelectProps<T> = {
  options: T[]
  maxResponses: number | null
  hasOtherOption: boolean
}

type SurveyMultipleChoiceProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<UiMultiSelectProps<Option> & FormItemWrapperProps, 'value' | 'defaultValue'>

const useOptionStyles = () => {
  const { token } = useUiBaseConfig()
  return {
    activeColor: { color: '#FFFFFF' },
    activeStyles: {
      backgroundColor: token.colorPrimary,
      border: `1px solid ${token.colorPrimary}`,
      color: '#FFFFFF',
    },
    baseStyles: {
      cursor: 'pointer',
      minHeight: '54px',
      borderRadius: token.borderRadiusLG,
      backgroundColor: '#FFFFFF',
      border: `1px solid ${token.colorBorder}`,
      padding: '16px',
    },
  }
}

export const SurveyMultipleChoice = <T extends FieldValues>(
  props: SurveyMultipleChoiceProps<T>
) => {
  const {
    formItemProps,
    inputProps: { value, onChange },
  } = useCustomController({
    ...props,
    name: `${props.name}.value` as Path<T>,
    defaultValue: [] as PathValue<T, Path<T>>,
  })

  const {
    inputProps: { value: otherValue, onChange: onOtherChange },
  } = useCustomController({
    ...props,
    name: `${props.name}.otherValue` as Path<T>,
    defaultValue: undefined as PathValue<T, Path<T>>,
  })

  const { options, maxResponses, hasOtherOption } = props

  const addOption = (event: React.MouseEvent, optionValue: string) => {
    event.preventDefault()
    const isNew = !value.includes(optionValue)

    const otherValueCount = otherValue ? 1 : 0

    if (maxResponses && value.length + otherValueCount >= maxResponses && isNew) {
      return onChange([...value.slice(0, -1), optionValue])
    }

    const newValue = isNew
      ? [...value, optionValue]
      : value.filter((o: string) => o !== optionValue)

    onChange(newValue)
  }

  const { formatMessage } = useIntl()
  const [isOtherOptionCompleted, setIsOtherOptionCompleted] = useState(false)

  const handleOtherValueChange = (newValue: string) => {
    if (newValue === '') {
      setIsOtherOptionCompleted(false)
      onOtherChange('')
      return
    }

    if (maxResponses && value.length >= maxResponses) {
      onChange([...value.slice(0, -1)])
    }

    onOtherChange(newValue)
  }

  const { activeColor, activeStyles, baseStyles } = useOptionStyles()

  return (
    <FormItem {...formItemProps}>
      <Flex vertical gap='small' className='mt-3'>
        {options.map(option => {
          const isActive = value?.includes(option.value)
          return (
            <Flex
              key={option.value}
              onClick={e => addOption(e, option.value)}
              style={{ ...baseStyles, ...(isActive ? activeStyles : {}) }}
              align='center'
              justify='space-between'
            >
              <Typography.Text style={isActive ? activeColor : {}} className='sensitive'>
                {option.label}
              </Typography.Text>
              {isActive && <UntitledIcon icon={ulCheckCircle} size={20} className='min-w-5 ml-2' />}
            </Flex>
          )
        })}

        {hasOtherOption && (
          <div
            style={{
              ...baseStyles,
              padding: 0,
              ...(isOtherOptionCompleted && otherValue ? activeStyles : {}),
            }}
          >
            <div className='relative w-full'>
              <Input
                value={otherValue ?? ''}
                onChange={e => handleOtherValueChange(e.target.value)}
                autoComplete='off'
                placeholder={formatMessage(messages.otherOption)}
                data-test='survey-other-option'
                name='other-option-input'
                variant='borderless'
                className='w-full pr-10 py-4'
                style={{
                  color: isOtherOptionCompleted && otherValue ? '#fff' : '#000',
                }}
                onFocus={() => setIsOtherOptionCompleted(false)}
                onBlur={() => setIsOtherOptionCompleted(!!otherValue)}
                maxLength={250}
              />
              {isOtherOptionCompleted && otherValue && (
                <UntitledIcon
                  icon={ulCheckCircle}
                  size={20}
                  className='absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none'
                />
              )}
            </div>
          </div>
        )}
      </Flex>
    </FormItem>
  )
}
